import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Check, ExternalLink, MapPin } from 'lucide-react';
import PortfolioImage from '../components/PortfolioImage';
import PublicPageShell from '../components/PublicPageShell';
import { useLanguage } from '../contexts/LanguageContext';
import { caseStudyPath, getLocalizedPublicCaseStudies } from '../data/contentLibrary';

const INTRO =
  "These are systems I've designed and built — some for my own ventures, some as capability demonstrations. Client case studies will be published as projects complete.";

const ProjectBadge = ({ type, children }) => (
  <span
    className={`inline-flex rounded-full border px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em] ${
      type === 'in-house'
        ? 'border-[#00FFD1]/35 bg-[#00FFD1]/10 text-[#7fffe8]'
        : 'border-white/15 bg-white/[0.045] text-white/62'
    }`}
  >
    {children}
  </span>
);

const WorkPage = () => {
  const { currentLang, t } = useLanguage();
  const caseStudies = getLocalizedPublicCaseStudies(currentLang);

  return (
    <PublicPageShell mainClassName="pt-20">
      <section className="relative overflow-hidden bg-black">
        <div className="hero-grid absolute inset-0 pointer-events-none" />
        <div className="relative mx-auto max-w-[1200px] px-5 py-16 md:px-[7.6923%] md:py-24">
          <nav
            className="mb-8 flex flex-wrap gap-2 text-sm text-white/44"
            aria-label={t('common.breadcrumb')}
          >
            <Link to="/" className="hover:text-[#00FFD1]">{t('nav.home')}</Link>
            <span>/</span>
            <span className="text-white/70">Work</span>
          </nav>

          <div className="max-w-4xl">
            <p className="mb-4 text-sm font-semibold uppercase tracking-[0.14em] text-[#00FFD1]">
              Selected work
            </p>
            <h1 className="text-4xl font-semibold leading-tight text-white md:text-6xl">
              Systems designed to make the next step easier
            </h1>
            <p className="mt-6 text-base leading-8 text-white/64 md:text-lg">
              {INTRO}
            </p>
          </div>

          <div className="mt-12 grid gap-5 lg:grid-cols-2">
            {caseStudies.map((caseStudy) => (
              <article
                key={caseStudy.slug}
                className={`group overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.03] transition-colors hover:border-[#00FFD1]/30 ${
                  caseStudy.featured ? 'lg:col-span-2 lg:grid lg:grid-cols-[1.2fr_1fr]' : ''
                }`}
              >
                <Link
                  to={caseStudyPath(caseStudy.slug)}
                  className="block overflow-hidden"
                  aria-label={`View ${caseStudy.title} project details`}
                >
                  <PortfolioImage
                    image={caseStudy.coverImage}
                    className={`transition-transform duration-500 group-hover:scale-[1.015] ${
                      caseStudy.featured ? 'h-full min-h-[280px]' : ''
                    }`}
                  />
                </Link>

                <div className="flex flex-col p-6 md:p-8">
                  <div className="flex flex-wrap items-center justify-between gap-3">
                    <ProjectBadge type={caseStudy.projectType}>{caseStudy.badge}</ProjectBadge>
                    <span className="flex items-center gap-1.5 text-xs text-white/42">
                      <MapPin className="h-3.5 w-3.5" aria-hidden="true" />
                      {caseStudy.city}
                    </span>
                  </div>

                  <p className="mt-6 text-xs uppercase tracking-[0.13em] text-white/38">
                    {caseStudy.industry}
                  </p>
                  <h2 className="mt-2 text-2xl font-semibold text-white md:text-3xl">
                    {caseStudy.title}
                  </h2>
                  <p className="mt-4 text-sm leading-7 text-white/62">
                    {caseStudy.summary}
                  </p>

                  <ul className="mt-6 space-y-3">
                    {caseStudy.features.map((feature) => (
                      <li key={feature} className="flex gap-3 text-sm leading-6 text-white/58">
                        <Check className="mt-0.5 h-4 w-4 flex-none text-[#00FFD1]" aria-hidden="true" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-7 flex flex-wrap gap-2" aria-label="Technologies used">
                    {caseStudy.technologies.map((technology) => (
                      <span
                        key={technology}
                        className="rounded-full border border-white/[0.08] px-2.5 py-1 text-[10px] text-white/38"
                      >
                        {technology}
                      </span>
                    ))}
                  </div>

                  <div className="mt-auto flex flex-wrap items-center gap-4 pt-8">
                    <Link
                      to={caseStudyPath(caseStudy.slug)}
                      className="inline-flex items-center gap-2 text-sm font-semibold text-[#7fffe8] hover:text-white"
                    >
                      View project <ArrowRight className="h-4 w-4" aria-hidden="true" />
                    </Link>
                    {caseStudy.demoUrl ? (
                      <a
                        href={caseStudy.demoUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-2 text-sm text-white/55 hover:text-white"
                      >
                        {caseStudy.demoButtonLabel}
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                      </a>
                    ) : (
                      <span className="text-xs text-white/30">Demo link coming soon</span>
                    )}
                  </div>
                </div>
              </article>
            ))}
          </div>

          <div className="mt-12 rounded-2xl border border-white/[0.08] bg-white/[0.025] p-6 md:flex md:items-center md:justify-between md:gap-8 md:p-8">
            <div>
              <h2 className="text-2xl font-semibold text-white">Have a system in mind?</h2>
              <p className="mt-2 max-w-2xl text-sm leading-6 text-white/55">
                Tell me what needs to be easier for your customers or your team.
              </p>
            </div>
            <Link to="/contact" className="btn-glass-primary mt-5 md:mt-0">
              Discuss your project
            </Link>
          </div>
        </div>
      </section>
    </PublicPageShell>
  );
};

export default WorkPage;
